import { useEffect, useState, useCallback, useRef, useMemo } from 'react'
import { useParams } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { ReactFlow, Controls, ControlButton, ReactFlowProvider, useReactFlow, useNodesInitialized, useNodesState, useEdgesState } from '@xyflow/react'
import type { Node, Edge, Connection, OnNodeDrag, FitViewOptions, Viewport } from '@xyflow/react'
import { Focus } from 'lucide-react'
import '@xyflow/react/dist/style.css'
// One latin face for each family, weight and style named by a text role that
// SeriesGraphEmbedFonts.test.ts finds in the code the embed route runs. That
// test fails when this list and those roles disagree, and its header says
// which code it reads and why latin.
import '@fontsource/manrope/latin-700.css'
import '@fontsource/manrope/latin-800.css'
import '@/styles/tribe.generated.css'
import '@/styles/series-graph-embed.css'
import { TribeMoveNode, NODE_WIDTH, NODE_HEIGHT } from '@/components/series/TribeMoveNode'
import type { MoveNodeData } from '@/components/series/MoveNode'
import { LoadingSpinner } from '@/components/shared/LoadingSpinner'
import { layoutNodes, getLayoutDirection } from '@/utils/layoutNodes'
import { useSeriesDetail } from '@/hooks/useSeriesDetail'
import { useSeriesConnections, useCreateConnection, useDeleteConnection } from '@/hooks/useMoveConnections'
import { useUpdateSeriesPositions } from '@/hooks/useUpdateSeriesPositions'
import { initApiClientWithToken, registerAuthErrorHandler } from '@/api/client'
import { queryKeys } from '@/api/queryKeys'
import type { SeriesMoveResponseDto } from '@/api/generated/api'
import type { SeriesMoveWithRelations } from '@/types/api-extensions'
import {
  WEB_TO_NATIVE_MESSAGE_TYPES,
  NATIVE_BRIDGE_METHODS,
  BRIDGE_PROTOCOL_VERSION,
} from '@/bridge/contract'

const nodeTypes = { moveNode: TribeMoveNode }

// The controls panel's own footprint from the canvas's top edge: 16px
// margin-top (--tribe-spacing-4) + 161px panel height (8px padding top and
// bottom, three 40px buttons, an 8px flex gap between the first two, and the
// divider's 17px gap-with-line between the last two: 8+40+8+40+17+40+8).
const CONTROLS_FOOTPRINT_PX = 177

// The fit button's options. `fitView` measures padding from the pane's edges
// and knows nothing about the panel drawn on top of it, so the top padding is
// the panel's footprint, or a fit can zoom a node in behind the panel. The top
// is a `px`-suffixed string because @xyflow/system's `parsePadding` reads that
// form as pixels. It reads a bare number n as a ratio instead, giving
// floor((pane - pane / (1 + n)) / 2) px per side, so 0.1 is about 4.5% of the
// pane. `Padding`'s object form reads a side it does not name as 0, not as
// `fitView`'s own default of 0.1, so the other three sides are named as 0.1.
const FIT_VIEW_OPTIONS = {
  padding: { top: `${CONTROLS_FOOTPRINT_PX}px`, right: 0.1, bottom: 0.1, left: 0.1 },
} satisfies FitViewOptions<Node<MoveNodeData>>

// Frame 1081:3720 opens the canvas at 1:1, not fitted: its leftmost node, the
// entry point (1081:3801), is 40 from the canvas's left edge and 39 below the
// controls panel (1081:3821), and the nodes after it run on past the right
// edge. The opening view puts the leftmost node there (the topmost of them,
// if several share that x). A graph with no nodes opens at the origin.
const OPENING_NODE_LEFT_PX = 40
const OPENING_NODE_GAP_BELOW_CONTROLS_PX = 39

function openingViewport(nodes: Node<MoveNodeData>[]): Viewport {
  const [anchor] = [...nodes].sort(
    (a, b) => a.position.x - b.position.x || a.position.y - b.position.y
  )
  if (!anchor) return { x: 0, y: 0, zoom: 1 }
  return {
    x: OPENING_NODE_LEFT_PX - anchor.position.x,
    y: CONTROLS_FOOTPRINT_PX + OPENING_NODE_GAP_BELOW_CONTROLS_PX - anchor.position.y,
    zoom: 1,
  }
}

function postToNative(payload: object) {
  window.ReactNativeWebView?.postMessage(JSON.stringify(payload))
}

// ── Inner canvas — lives inside ReactFlowProvider so useReactFlow() works ──
// Exported for the browser layout fixture, src/test/layout/series-graph-canvas.tsx.

interface CanvasProps {
  id: string
  nodes: Node<MoveNodeData>[]
  edges: Edge[]
  isEditMode: boolean
  onNodeClick: (e: React.MouseEvent, node: Node<MoveNodeData>) => void
}

export function SeriesGraphCanvas({ id, nodes: derivedNodes, edges: derivedEdges, isEditMode, onNodeClick }: CanvasProps) {
  const { getNode, fitView } = useReactFlow<Node<MoveNodeData>>()
  const { mutate: createConnection } = useCreateConnection()
  const { mutate: updatePositions } = useUpdateSeriesPositions({ skipInvalidation: true })

  // Mirror the derived nodes/edges into local state so React Flow's drag
  // updates are reflected immediately. Without this, dragging a node updates
  // the server (via onNodeDragStop) but the visual snaps back because the
  // memoized `nodes` prop hasn't changed.
  const [nodes, setNodes, onNodesChange] = useNodesState<Node<MoveNodeData>>(derivedNodes)
  const [edges, setEdges, onEdgesChange] = useEdgesState<Edge>(derivedEdges)
  // Computed once. React Flow reads `defaultViewport` only when its pane
  // mounts, so a value computed on a later render would be ignored.
  const [defaultViewport] = useState(() => openingViewport(derivedNodes))
  useEffect(() => { setNodes(derivedNodes) }, [derivedNodes, setNodes])
  useEffect(() => { setEdges(derivedEdges) }, [derivedEdges, setEdges])

  // Tracks whether a drag started while in edit mode. Survives an edit-mode
  // toggle so an in-flight position still gets persisted on drag-stop, even
  // if the user taps "Save" in the native header mid-gesture.
  const dragStartedInEditModeRef = useRef(false)

  // Tell native the graph has first drawn, so it can lift its skeleton. React
  // Flow reports nodes initialized once it has measured every node, and never
  // for a graph with no nodes, which has nothing to measure. It flips back to
  // false whenever a node is added, hence the ref: sent once per canvas mount.
  const nodesInitialized = useNodesInitialized()
  const graphRenderedPostedRef = useRef(false)
  useEffect(() => {
    if (graphRenderedPostedRef.current) return
    if (derivedNodes.length > 0 && !nodesInitialized) return
    graphRenderedPostedRef.current = true
    postToNative({ type: WEB_TO_NATIVE_MESSAGE_TYPES.graphRendered })
  }, [derivedNodes.length, nodesInitialized])

  const onConnect = useCallback((connection: Connection) => {
    if (!connection.source || !connection.target) return
    const sourceNode = getNode(connection.source)
    const targetNode = getNode(connection.target)
    if (!sourceNode || !targetNode) return
    createConnection(
      { seriesId: id, fromMoveId: sourceNode.data.moveId, toMoveId: targetNode.data.moveId },
      { onSuccess: () => postToNative({ type: WEB_TO_NATIVE_MESSAGE_TYPES.connectionCreated, seriesId: id }) }
    )
  }, [getNode, id, createConnection])

  const onEdgeClick = useCallback((_e: React.MouseEvent, edge: Edge) => {
    postToNative({ type: WEB_TO_NATIVE_MESSAGE_TYPES.requestDeleteConnection, connectionId: edge.id })
  }, [])

  const onNodeDragStart: OnNodeDrag<Node<MoveNodeData>> = useCallback(() => {
    if (isEditMode) dragStartedInEditModeRef.current = true
  }, [isEditMode])

  const onNodeDragStop: OnNodeDrag<Node<MoveNodeData>> = useCallback((_e, node) => {
    if (!dragStartedInEditModeRef.current) return
    dragStartedInEditModeRef.current = false
    updatePositions(
      { seriesId: id, data: { positions: [{ seriesMoveId: node.id, positionX: node.position.x, positionY: node.position.y }] } },
      { onSuccess: () => postToNative({ type: WEB_TO_NATIVE_MESSAGE_TYPES.nodePositionUpdated, seriesId: id }) }
    )
  }, [id, updatePositions])

  return (
    <div style={{ width: '100vw', height: '100vh' }}>
      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        nodeTypes={nodeTypes}
        onNodeClick={onNodeClick}
        nodesDraggable={isEditMode}
        nodesConnectable={isEditMode}
        elementsSelectable={isEditMode}
        nodeDragThreshold={0}
        onConnect={isEditMode ? onConnect : undefined}
        onEdgeClick={isEditMode ? onEdgeClick : undefined}
        onNodeDragStart={onNodeDragStart}
        onNodeDragStop={onNodeDragStop}
        defaultViewport={defaultViewport}
        minZoom={0.2}
        maxZoom={2}
        defaultEdgeOptions={{ type: 'smoothstep', animated: false }}
      >
        <Controls position="top-left" showInteractive={false} showFitView={false}>
          {/* React Flow's built-in fitview glyph is brackets only; the frame
              draws brackets around a centre circle, which is lucide-react's
              `Focus` icon verbatim (same four corner paths as `Scan`, plus
              the circle) — no new icon drawn. `className` keeps this on the
              same `.react-flow__controls-fitview` selector the CSS and tests
              already target. */}
          <ControlButton
            className="react-flow__controls-fitview"
            onClick={() => fitView(FIT_VIEW_OPTIONS)}
            title="fit view"
            aria-label="fit view"
          >
            <Focus />
          </ControlButton>
        </Controls>
      </ReactFlow>
    </div>
  )
}

// ── Outer page — owns state, auth, data fetching ──────────────────────────

export function SeriesGraphEmbedPage() {
  const { id } = useParams<{ id: string }>()
  const { mutate: deleteConnection } = useDeleteConnection()
  const queryClient = useQueryClient()

  // The page mounts tokenless. The native side delivers the access token over
  // the private bridge channel (setToken) after the bridgeReady handshake —
  // it is never read from a page global. Queries stay disabled until it lands.
  const [token, setToken] = useState<string | undefined>(undefined)

  const [isEditMode, setIsEditMode] = useState(false)

  useEffect(() => {
    registerAuthErrorHandler(() => postToNative({ type: WEB_TO_NATIVE_MESSAGE_TYPES.authError }))
    return () => registerAuthErrorHandler(null)
  }, [])

  useEffect(() => {
    window.__wgBridge = {
      [NATIVE_BRIDGE_METHODS.setToken]: (t) => {
        initApiClientWithToken(t)
        setToken(t)
      },
      [NATIVE_BRIDGE_METHODS.setEditMode]: (enabled) => setIsEditMode(Boolean(enabled)),
      // Native calls this after it changes the series itself: a move added
      // or removed, or a connection deleted from its Connections sheet. The
      // graph's nodes come from the series detail and its edges from the
      // series' connections, so both queries are invalidated, which refetches
      // each at once while the page is using it. Invalidating only the detail
      // left a natively deleted connection drawn: a query that has gone stale
      // is not refetched until something triggers it (a remount, window focus
      // or a reconnect), and inside the WebView none of those may come.
      [NATIVE_BRIDGE_METHODS.invalidateSeries]: () => {
        void queryClient.invalidateQueries({ queryKey: queryKeys.series.detail(id ?? '') })
        void queryClient.invalidateQueries({
          queryKey: queryKeys.moveConnections.bySeries(id ?? ''),
        })
      },
      [NATIVE_BRIDGE_METHODS.confirmDeleteConnection]: (connectionId) =>
        deleteConnection(connectionId, {
          onSuccess: () => postToNative({ type: WEB_TO_NATIVE_MESSAGE_TYPES.connectionDeleted, seriesId: id ?? '' }),
        }),
    }
    postToNative({ type: WEB_TO_NATIVE_MESSAGE_TYPES.bridgeReady, protocolVersion: BRIDGE_PROTOCOL_VERSION })
    return () => {
      delete window.__wgBridge
    }
  }, [queryClient, deleteConnection, id])

  const onNodeClick = useCallback((_e: React.MouseEvent, node: Node<MoveNodeData>) => {
    postToNative({
      type: WEB_TO_NATIVE_MESSAGE_TYPES.showMoveInfo,
      seriesMoveId: node.id,
      moveId: node.data.moveId,
      name: node.data.name,
      description: node.data.description,
      thumbnailUrl: node.data.thumbnailUrl,
      url: node.data.url,
    })
  }, [])

  const { data: series, isLoading: seriesLoading } = useSeriesDetail(
    token ? (id ?? '') : ''
  )
  const { data: connections, isLoading: connectionsLoading } = useSeriesConnections(
    token ? (id ?? '') : ''
  )

  const { nodes, edges } = useMemo<{
    nodes: Node<MoveNodeData>[]
    edges: Edge[]
  }>(() => {
    const empty: { nodes: Node<MoveNodeData>[]; edges: Edge[] } = { nodes: [], edges: [] }
    if (!series?.seriesMoves) {
      return empty
    }

    // Keeps the series-moves whose `move` came back; the generated
    // `SeriesMoveResponseDto` marks it optional and nullable. The narrowed
    // type takes the move's shape from `SeriesMoveWithRelations['move']` in
    // api-extensions.ts rather than re-declaring it. A type predicate's type
    // must be assignable TO its parameter's type (TS2677), and an intersection
    // with `SeriesMoveResponseDto` always is, so this needs no `as`.
    const seriesMoves = series.seriesMoves.filter(
      (sm): sm is SeriesMoveResponseDto & { move: NonNullable<SeriesMoveWithRelations['move']> } =>
        sm.move != null
    )

    if (seriesMoves.length === 0) {
      return empty
    }

    const nodeData: Node<MoveNodeData>[] = seriesMoves.map((sm) => ({
      id: sm.id,
      type: 'moveNode',
      position:
        sm.positionX != null && sm.positionY != null
          ? { x: sm.positionX, y: sm.positionY }
          : { x: 0, y: 0 },
      data: {
        moveId: sm.move.id,
        name: sm.move.name,
        description: sm.move.description,
        thumbnailUrl: sm.move.thumbnailUrl,
        url: sm.move.url,
        tagName: sm.move.taggings?.[0]?.tag?.name ?? null,
      },
    }))

    const connectionList = Array.isArray(connections) ? connections : []
    const edgeData: Edge[] = connectionList.flatMap((conn) => {
      const src = seriesMoves.find((sm) => sm.move.id === conn.fromMoveId)
      const tgt = seriesMoves.find((sm) => sm.move.id === conn.toMoveId)
      if (!src || !tgt) return []
      return [{
        id: conn.id,
        source: src.id,
        target: tgt.id,
        type: 'smoothstep',
        animated: false,
        markerEnd: { type: 'arrowclosed', color: 'var(--tribe-color-neutral6)' },
      } satisfies Edge]
    })

    const unpositionedIds = new Set(
      seriesMoves
        .filter((sm) => sm.positionX == null || sm.positionY == null)
        .map((sm) => sm.id)
    )

    if (unpositionedIds.size === 0) {
      return { nodes: nodeData, edges: edgeData }
    }

    const savedPositions = new Map(
      seriesMoves
        .filter((sm): sm is typeof sm & { positionX: number; positionY: number } =>
          sm.positionX != null && sm.positionY != null
        )
        .map((sm) => [sm.id, { x: sm.positionX, y: sm.positionY }])
    )
    const dagred = layoutNodes(nodeData, edgeData, {
      direction: getLayoutDirection(window.innerWidth),
      nodeWidth: NODE_WIDTH,
      nodeHeight: NODE_HEIGHT,
    })
    const finalNodes = dagred.map((n) => {
      const saved = savedPositions.get(n.id)
      return saved ? { ...n, position: saved } : n
    })

    return { nodes: finalNodes, edges: edgeData }
  }, [series, connections])

  // Tokenless mount is the normal pre-handshake state: show the loading
  // spinner while waiting for the native side to deliver the token via
  // setToken. The native side owns the genuine auth-failure UI (it surfaces
  // its error screen if the handshake/token never arrives within its load
  // timeout); the authError post below covers a failed client init.
  //
  // `.tribe-scope` carries the Tribe custom properties (tribe.generated.css)
  // on the page's single outermost element, covering both the loading and
  // canvas states — never on :root, since this embed shares globals.css and
  // its shadcn variables with every other route in the web app.
  return (
    <div className="tribe-scope h-screen w-screen">
      {!token || seriesLoading || connectionsLoading ? (
        <div className="flex h-screen items-center justify-center bg-[var(--tribe-color-backgroundBrand)]">
          <LoadingSpinner className="border-[var(--tribe-color-brandPrimary)] border-t-transparent" />
        </div>
      ) : (
        <ReactFlowProvider>
          <SeriesGraphCanvas
            id={id ?? ''}
            nodes={nodes}
            edges={edges}
            isEditMode={isEditMode}
            onNodeClick={onNodeClick}
          />
        </ReactFlowProvider>
      )}
    </div>
  )
}
